import { FC, ReactElement, memo, useCallback, useMemo } from 'react';
import { mealChoiceEditModalStore } from '../../store/mealChoiceEditModalStore';
import integrationsStore from '~/mobx/integrationStore';
import { IMealItem } from '~/types/combo';
import { isDisabledByPos } from '~/utils/helpers';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import LazyProductsPicker from '~/view/components/Controls/Pickers/LazyProductsPicker/LazyProductsPicker';
import { transformToPickerOption } from '~/view/components/Controls/Pickers/Picker/helpers';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import IntegrationManager from '~/view/components/IntegrationManager/IntegrationManager';
import { ProductWarning } from '~/view/components/ProductWarning';
import Text from '~/view/components/Text/Text';
import { localeStore } from '~/mobx/localesStore';

interface IProps {
  item?: IMealItem;
  sizeId: number;
}

export const MealChoiceEditModalItemPicker: FC<IProps> = memo((props: IProps): ReactElement => {
  const { comboMealChangeItemInItemChoice } = integrationsStore.posRestrictions || {};

  const showOpenMealItemMdfExclModalButton = useMemo((): boolean => {
    return mealChoiceEditModalStore.showOpenMealItemMdfExclModalButton(props.item);
  }, [props.item]);

  const selectedOptions = useMemo((): IPickerOption[] => {
    return props.item ? [transformToPickerOption(props.item)] : [];
  }, [props.item]);

  const onOpenMealItemMdfExclModal = useCallback(() => {
    if (!props.item) return;
    mealChoiceEditModalStore.onOpenMealItemMdfExclModal(props.item);
  }, [props.item]);

  const onSelectItem = useCallback(
    (options: IPickerOption[]) => {
      mealChoiceEditModalStore.onSelectItem(options, props.sizeId);
    },
    [props.sizeId]
  );

  return (
    <div className="d-flex flex-column mt-4">
      <div className="d-flex flex-row align-items-center">
        <Text
          type="block-header"
          text="txt_item"
          disabled={isDisabledByPos(comboMealChangeItemInItemChoice)}
          tooltip={{
            message: 'mc_nosize_nomodifier',
            duration: TOOLTIP_DURATION.EXTRA_LARGE,
            position: TOOLTIP_POSITION.PORTAL,
            minWidth: 370,
            iconSize: 16,
          }}
        />

        <ProductWarning comboItem={props.item} type="mealItemsInEditPage" iconSize={18} marginLeft={3} position={TOOLTIP_POSITION.PORTAL} />
      </div>

      <IntegrationManager
        setting={comboMealChangeItemInItemChoice}
        wrapperClassName="mt-2"
        tooltipOptions={{ position: TOOLTIP_POSITION.PORTAL }}
        withTooltip
      >
        <LazyProductsPicker
          searchable
          selectedOptions={selectedOptions}
          onSelect={onSelectItem}
          returnFullData={mealChoiceEditModalStore.onSetFullItemsList}
          position="portal"
          markUnavailable
          unavailableType="productForMeal"
          disableUnavailable
          className="full-width"
          listClassName="full-width"
        />
      </IntegrationManager>

      {showOpenMealItemMdfExclModalButton && (
        <button onClick={onOpenMealItemMdfExclModal} className="meal-edit-modal__btn-open-mdf-excl-modal mt-2">
          {localeStore.t('cm_config_page_show_modifiers_and_exclusions')}
        </button>
      )}
    </div>
  );
});
